import * as R from 'ramda'
import {MAX_CARDS_IN_HAND} from '@kapitalist/contract/ruleset'
import type {
  TCardData,
  TPastGamePosition,
  TGamePlayer,
  TPlayerId,
} from '@kapitalist/contract/types'
import {noop} from '@kapitalist/utils/function'
import {safely} from '@kapitalist/utils/safely'
import {BY_ITSELF_PORTFOLIO_NEIGHBORHOOD_ID} from '~/game/types/neighborhoodId'
import {DataBackedModel} from '~/models/base'
import {majorActionFactory, minorActionFactory} from '~/models/game/factories'
import GamePosition from '~/models/game/GamePosition'
import type MajorAction from '~/models/game/MajorAction'
import type {AnyMinorAction} from '~/models/game/types'

export default class PastGamePosition extends DataBackedModel<TPastGamePosition> {
  gamePosition: GamePosition

  // TODO the types allow both to be missing, not sure if that would ever make sense though.
  majorActionTaken?: MajorAction
  minorActionTaken?: AnyMinorAction
  date: Date

  constructor(
    data: TPastGamePosition,
    players: TGamePlayer[],
    currentPlayerId: TPlayerId
  ) {
    super(data)
    this.date = new Date(data.timestamp)
    this.gamePosition = GamePosition.build(
      data.gamePosition,
      players,
      currentPlayerId
    )

    const majorActionData = data.majorActionTaken
    switch (majorActionData?.type) {
      case 'changeNeighborhood':
      case 'playToNeighborhood': {
        // `unambiguousNeighborhoodDescription` can't handle the fact that the GamePosition we have
        // (which is from before this action took place) doesn't know about the neighborhood
        // that a card was moved to, for cards that moved to a new neighborhood.
        // To work around this, we can just swap the PNId for the sentinel, which that code
        // renders as the skin's "its own <neighborhood>". A changeNeighborhood that starts a new set of a
        // color carries no PNId and describes itself, so there's nothing to fix up for it.
        const pnId =
          majorActionData.type === 'playToNeighborhood'
            ? majorActionData.portfolioNeighborhoodId
            : majorActionData.destination.kind === 'existingNeighborhood'
              ? majorActionData.destination.portfolioNeighborhoodId
              : undefined

        const knownToSnapshot = this.gamePosition.portfolios.some(p =>
          p.neighborhoods.some(pn => pn.id === pnId)
        )

        if (pnId !== undefined && !knownToSnapshot) {
          console.log(
            'Removing a reference to a PNId so the game log doesnt crash: ',
            pnId,
            majorActionData,
            this.gamePosition
          )
          if (majorActionData.type === 'playToNeighborhood') {
            majorActionData.portfolioNeighborhoodId =
              BY_ITSELF_PORTFOLIO_NEIGHBORHOOD_ID
          } else {
            majorActionData.destination = {
              kind: 'existingNeighborhood',
              portfolioNeighborhoodId: BY_ITSELF_PORTFOLIO_NEIGHBORHOOD_ID,
            }
          }
        }
        break
      }
      case 'discard': {
        // the backend sends back an empty array intentionally, but we can work out the number of cards
        // that were discarded here. nothing reads the card data here so faking it seems alright for now
        const excessCardCount = Math.max(
          0,
          this.gamePosition.majorActorPlayer.hand.cardCount() - MAX_CARDS_IN_HAND
        )
        majorActionData.cards = R.times<TCardData>(
          i => ({
            id: `passGo#${i}`,
            type: 'passGo',
            version: i,
          }),
          excessCardCount
        )
        break
      }
      default:
        noop()
    }

    this.majorActionTaken = safely(majorActionData, m =>
      majorActionFactory(m, this.gamePosition.majorActorPlayer.portfolio, {
        lastRentThisVisit: this.gamePosition.lastRentThisVisit,
      })
    )
    this.minorActionTaken = safely(data.minorActionTaken, minorActionFactory)
  }
}
